import type { FetchResult } from '@apollo/client';
import { useCallback } from 'react';

import { attachScanToProjectUpdater } from 'src/shared/graphql/cache/Project';
import type {
  AttachScanToProjectMutation,
  BasicJobInfoFragment,
} from 'src/shared/graphql/generated';
import { useAttachScanToProjectMutation } from 'src/shared/graphql/generated';
import { logError } from 'src/shared/telemetry/logger';

export const useAttachScanToProjectHandler = (
  projectId: string,
  scan: BasicJobInfoFragment,
): [
  () => Promise<
    | FetchResult<
        AttachScanToProjectMutation,
        Record<string, any>,
        Record<string, any>
      >
    | undefined
  >,
] => {
  const [attachScanToProject] = useAttachScanToProjectMutation();

  const attachScanToProjectHandler = useCallback(async () => {
    if (!projectId) return undefined;

    const attachResult = await attachScanToProject({
      variables: {
        projectId,
        scanId: scan.id,
      },
      update: attachScanToProjectUpdater({
        projectIdToUpdate: Number(projectId),
        scan,
      }),
    });

    if (attachResult.errors?.length) {
      attachResult.errors.forEach(e => logError(e));
      throw new Error('Failed to attach scan to project');
    }

    return attachResult;
  }, [attachScanToProject, projectId, scan]);

  return [attachScanToProjectHandler];
};
